import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, Eye, EyeOff } from 'lucide-react';

import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { ItemRow } from '@/components/stashstr/ItemRow';
import { useStashstrFeed, type FeedSort, type TopMetric } from '@/hooks/useStashstrFeed';
import { HIDE_UNPROVEN_DEFAULT, readHideUnproven, writeHideUnproven } from '@/lib/stashstr';

interface FeedProps {
  territory?: string;
  sort: FeedSort;
  title?: string;
  since?: number;
  topMetric?: TopMetric;
}

export function Feed({ territory, sort, title, since, topMetric }: FeedProps) {
  const [limit, setLimit] = useState(20);
  const [hideUnproven, setHideUnproven] = useState(() => {
    try {
      return readHideUnproven();
    } catch {
      return HIDE_UNPROVEN_DEFAULT;
    }
  });
  const { data, isLoading } = useStashstrFeed({ territory, sort, limit, since, topMetric });

  const entries = data?.entries ?? [];
  const proven = entries.filter((e) => e.powValid);
  const unproven = entries.filter((e) => !e.powValid);
  // Toggling hiding off reveals ALL unproven posts immediately (fully
  // expanded); there is no collapsed/expand step.
  const visible = hideUnproven ? proven : entries;

  const hiddenCount = unproven.length;

  const toggleHide = (checked: boolean) => {
    setHideUnproven(checked);
    try {
      writeHideUnproven(checked);
    } catch {
      // localStorage unavailable — ignore
    }
  };

  return (
    <div>
      {title && (
        <h1 className="text-[18px] font-bold text-gray-900 dark:text-gray-100 mb-3">{title}</h1>
      )}

      {/* Hide-unproven toggle */}
      <div className="flex items-center justify-end gap-2 mb-2 text-[12px] text-gray-500 dark:text-gray-400">
        <label htmlFor="hide-unproven" className="flex items-center gap-1.5 cursor-pointer">
          {hideUnproven ? <EyeOff className="size-3.5" /> : <Eye className="size-3.5" />}
          Hide unproven (PoW)
        </label>
        <Switch
          id="hide-unproven"
          checked={hideUnproven}
          onCheckedChange={toggleHide}
          aria-label="Hide unproven posts"
        />
      </div>

      {isLoading ? (
        <div className="space-y-0">
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="rounded-md border border-gray-200 dark:border-[#2b2f34] bg-[#f6f7f9] dark:bg-[#1e2023] p-3 mb-2 flex gap-2"
            >
              <div className="flex flex-col items-center gap-1 shrink-0 w-9">
                <Skeleton className="h-6 w-6" />
                <Skeleton className="h-3 w-8" />
                <Skeleton className="h-6 w-6" />
              </div>
              <div className="space-y-2 flex-1">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            </div>
          ))}
        </div>
      ) : visible.length === 0 && (hideUnproven || hiddenCount === 0) ? (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center text-muted-foreground">
            No posts yet. Be the first to{' '}
            <Link to="/post" className="text-[#1a56db] dark:text-[#6ea8fe] hover:underline">
              post
            </Link>
            .
          </CardContent>
        </Card>
      ) : (
        <div>
          {visible.map((entry) => (
            <ItemRow key={entry.event.id} entry={entry} />
          ))}

          {hideUnproven && hiddenCount > 0 && (
            <div className="py-2 text-center">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-[12px] text-gray-500 dark:text-gray-400"
                onClick={() => toggleHide(false)}
              >
                <ChevronDown className="size-3.5 mr-1" />+ {hiddenCount} hidden (unproven). Show
              </Button>
            </div>
          )}

          {/* Feed capped at 100 events; no cursor paging v1 */}
          {visible.length >= limit && limit < 100 && (
            <div className="py-4 text-center">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setLimit((l) => l + 30)}
                className="text-[13px]"
              >
                more
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
